import { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { Modal, Button, Field, TextInput, tokens } from '@hotjar/hotsauce';

const StyledField = styled(Field)`
  width: 100%;
  padding: ${tokens.spacer.x3} 0;

  // FIXME: remove this overwrite of insights.css
  label {
    text-transform: unset;
  }
`;

interface ManageCollectionModalProps {
  editedCollection: { name: string } | null;
  error: string | undefined;
  isOpen: boolean;
  isLoading: boolean;
  loadingText: string;
  onCancel: () => void;
  onConfirm: (collectionName: string) => void;
  resetError: () => void;
}

export const ManageCollectionModal = ({
  editedCollection,
  error,
  isOpen,
  isLoading,
  loadingText,
  onCancel,
  onConfirm,
  resetError,
}: ManageCollectionModalProps): JSX.Element => {
  const { t } = useTranslation();
  const [collectionName, setCollectionName] = useState(editedCollection?.name ?? '');

  const id = 'collection-name-input';
  const describedBy = error ? `${id}-helper` : `${id}-helper ${id}-invalid`;
  const modalContentLabel = editedCollection
    ? t('collections:manage-modal.content-label-edit')
    : t('collections:manage-modal.content-label-create');
  const modalTitle = editedCollection
    ? t('collections:manage-modal.edit-title')
    : t('collections:manage-modal.create-title');
  const modalConfirmButtonLabel = editedCollection
    ? t('collections:manage-modal.save-changes')
    : t('collections:manage-modal.create-title');

  const handleChange = useCallback(
    (e: { target: { value: string } }) => {
      setCollectionName(e.target.value);
      resetError();
    },
    [resetError, setCollectionName],
  );
  const handleConfirmAction = useCallback(() => {
    onConfirm(collectionName);
  }, [collectionName, onConfirm]);

  return (
    <Modal contentLabel={modalContentLabel} isOpen={isOpen} onClose={onCancel} size="s">
      <Modal.Header title={modalTitle} />
      <Modal.Body>
        <StyledField
          inputId={id}
          labelText={t('collections:manage-modal.input-label')}
          invalidText={error}
          isInvalid={Boolean(error)}
        >
          <TextInput
            id={id}
            placeholder={t('collections:manage-modal.input-placeholder')}
            value={collectionName}
            onChange={handleChange}
            isDisabled={isLoading}
            isInvalid={Boolean(error)}
            isRequired
            aria-describedby={describedBy}
          />
        </StyledField>
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" isDisabled={isLoading} onClick={onCancel}>
          {t('collections:manage-modal.cancel')}
        </Button>
        <Button
          kind="primary"
          onClick={handleConfirmAction}
          isLoading={isLoading}
          isDisabled={!collectionName || !!error}
          loadingText={loadingText}
        >
          {modalConfirmButtonLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
